import { Injectable, computed, signal } from '@angular/core';

/**
 * Width the open AI assistant drawer takes from the layout.
 *
 * `overa-ai-chat-panel` is a drawer fixed to the right edge of the viewport, on top of the page. On
 * its own it covered the right side of whatever screen opened it — the editor's action icons and
 * the scrollbar of the layout's scroll container (`.main-content-overa`). The layout reserves this
 * width as right padding of `.master-container-overa`, which narrows the scroll container so its
 * content and scrollbar end where the drawer starts (same approach as Activity's layout).
 *
 * Several panels may report at once (a screen and a dialog); the widest one wins.
 */
@Injectable({ providedIn: 'root' })
export class AiDrawerSpaceService {
  private readonly widths = new Map<object, number>();
  private readonly _width = signal(0);
  private readonly _animating = signal(false);
  private animationTimer?: ReturnType<typeof setTimeout>;

  /** Width to reserve, in px (0 = no drawer open). */
  readonly width = this._width.asReadonly();

  /**
   * True for a moment when the drawer opens or closes, so the layout can animate the padding along
   * with the drawer. Not while dragging its resize handle: there the content must follow the pointer.
   */
  readonly animating = this._animating.asReadonly();

  readonly reserved = computed(() => this._width() > 0);

  /** Reports the width of `owner`'s drawer (0 when closed). */
  set(owner: object, width: number): void {
    if (width > 0) this.widths.set(owner, width);
    else this.widths.delete(owner);

    const next = this.widths.size ? Math.max(...this.widths.values()) : 0;
    const previous = this._width();
    if (next === previous) return;

    if (previous === 0 || next === 0) {
      this._animating.set(true);
      clearTimeout(this.animationTimer);
      this.animationTimer = setTimeout(() => this._animating.set(false), 300);
    }
    this._width.set(next);
  }

  /** `owner` is gone (e.g. its screen was left with the drawer open): drop its reservation. */
  release(owner: object): void {
    this.set(owner, 0);
  }
}
